import { useEffect, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plug, Loader2, Save, RefreshCw } from "lucide-react";
import { getSettings, updateSettings } from "../../../services/dataService";
import { PageHeader } from "../../PageHeader";
import { StatusBadge } from "../../StatusBadge";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Label } from "../../ui/label";
import { formatApiErrorDetail } from "../../../lib/api";
import { formatDateTime } from "../../../lib/format";
import { toast } from "sonner";

const FIELDS = [
  { name: "client_id", label: "Client ID", placeholder: "Xero OAuth2 App Client ID" },
  { name: "client_secret", label: "Client Secret", placeholder: "••••••••", type: "password" },
  { name: "tenant_id", label: "Tenant ID", placeholder: "Xero organisation tenant ID" },
  { name: "redirect_uri", label: "Redirect URI", placeholder: "https://…/xero/callback" },
  { name: "webhook_key", label: "Webhook Signing Key", placeholder: "Webhook verification key" },
];

export function XeroConnector() {
  const qc = useQueryClient();
  const { data, isLoading } = useQuery({ queryKey: ["settings", "xero"], queryFn: () => getSettings("xero") });
  const [form, setForm] = useState(null);

  useEffect(() => { if (data) setForm(data); }, [data]);

  const mut = useMutation({
    mutationFn: (payload) => updateSettings("xero", payload),
    onSuccess: (res) => { qc.setQueryData(["settings", "xero"], res); setForm(res); toast.success("Xero settings saved"); },
    onError: (e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Save failed"),
  });

  if (isLoading || !form) return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-blue-600" /></div>;

  return (
    <div>
      <PageHeader title="Xero Connector" icon={Plug} description="OAuth2 credentials, tenant and webhook configuration for pulling awaiting-payment invoices.">
        <StatusBadge status={form.connected ? "Processed & Cleared" : "Not Retried"} />
      </PageHeader>

      <div className="max-w-2xl rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-6 shadow-sm space-y-5" data-testid="xero-connector-form">
        <div className="flex items-center gap-2 rounded-lg bg-sky-50 dark:bg-sky-950/40 border border-sky-200 dark:border-sky-800 px-3 py-2 text-xs text-sky-700 dark:text-sky-300">
          <RefreshCw className="h-3.5 w-3.5" />
          {form.last_sync ? `Last synced ${formatDateTime(form.last_sync)}` : "No sync performed yet."}
        </div>
        {FIELDS.map((f) => (
          <div className="space-y-2" key={f.name}>
            <Label>{f.label}</Label>
            <Input
              type={f.type || "text"}
              value={form[f.name] || ""}
              placeholder={f.placeholder}
              onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
              data-testid={`xero-${f.name}`}
            />
          </div>
        ))}
        <Button onClick={() => mut.mutate(form)} disabled={mut.isPending} className="bg-slate-900 hover:bg-slate-800 dark:bg-blue-600 text-white font-semibold" data-testid="xero-save">
          {mut.isPending ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Save className="h-4 w-4 mr-2" /> Save Xero Settings</>)}
        </Button>
      </div>
    </div>
  );
}
